/**
 * Single source of truth for route paths. Import these instead of writing
 * string literals at call sites, so a URL change is a one-line edit here.
 *
 * ## What may go in a URL
 *
 * A path is the least private thing about a request. It is kept in browser
 * history, read by extensions, sent as `Referer` on the way to another origin,
 * and caught in any screenshot of the address bar — all of it outside the
 * panel's control and none of it cleared by signing out. This console lists
 * every Naurist customer, so a route param carries an opaque id and nothing
 * read off the record: no name, no email, no phone.
 *
 * None of this is the actual access control. The server decides that. These
 * keep a URL from being a place to start looking.
 */
import { encodeRouteId } from "./routeIds";

export const ROUTES = {
  // Auth — two steps, the same two the panel has always had: the credentials,
  // then the six digits sent to the address they belong to.
  login: "/login",
  verifyOtp: "/otp-verification",

  // App
  dashboard: "/dashboard",
  users: "/users",
  /** One account, opened from a row of the roster. `:userId` is a token, not
   *  the id itself — see `userDetailPath` and `routeIds`. */
  userDetail: "/users/:userId",
  revenue: "/revenue",
  settings: "/settings",
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** What the browser tab says on each screen. */
export const ROUTE_TITLES: Record<string, string> = {
  [ROUTES.login]: "Sign in",
  [ROUTES.verifyOtp]: "Two step verification",
  [ROUTES.dashboard]: "Dashboard",
  [ROUTES.users]: "Users",
  [ROUTES.revenue]: "Revenue",
  [ROUTES.settings]: "Settings",
  // No entry for `userDetail`, and none wanted: titles are matched by prefix,
  // so one account's screen is titled "Users" like the list it came from.
  // Naming the account instead would put a customer's name in the window
  // title, the task switcher and any screenshot of either — which is the same
  // thing keeping it out of the URL was for.
};

/**
 * The detail screen for one account — `userDetailPath(user.id)`.
 *
 * The id goes in enciphered, so the path does not read as a record to anyone
 * who happens to see the address bar, and so the sequential key it stands for
 * is not a number to add one to. Pass the plain id; `UserDetailPage` decodes
 * it back with `decodeRouteId`. Call sites deal in real ids only, and no call
 * site should encode one itself.
 *
 * The tab is a `?tab=` rather than a path of its own: it names a tab, not a
 * record, so it is allowed in the URL in the clear — and a link can point at
 * one, which is what lets a refresh land back where somebody was reading.
 */
export const userDetailPath = (userId: number | string, tab?: string) => {
  const token = encodeRouteId(String(userId));
  return tab ? `${ROUTES.users}/${token}?tab=${tab}` : `${ROUTES.users}/${token}`;
};

/** Marketing-site pages that live outside this panel. */
export const EXTERNAL_LINKS = {
  privacyPolicy: "https://naurist.com/privacy-policy",
  termsOfUse: "https://naurist.com/terms-of-use",
} as const;
